import {
  useCreateModifiableList,
  useModifiableList,
} from "@/api/modifiableList";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { z } from "zod";
import TextField from "../form-fields/TextField";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { SupportedLocale } from "@/i18n";
import { normalizeModifiableTagKey } from "@/lib/modifiable-multi-select";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";

const schema = z.object({
  value: z.string(),
});

type SchemaType = z.infer<typeof schema>;

const AddTagToModifiableList = ({
  listKey,
  toggleOption,
  lang,
}: {
  listKey: string | undefined;
  toggleOption: (value: string) => void;
  lang: SupportedLocale;
}) => {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const { toast } = useToast();

  const form = useForm<SchemaType>({
    resolver: zodResolver(schema),
  });

  const { mutate: createModifiableList } = useCreateModifiableList(listKey);
  const { data: existingOptions } = useModifiableList(listKey, lang);

  const { control, getValues } = form;

  const onSubmit = async (data: SchemaType) => {
    if (!listKey) return;
    const value = data.value.trim();
    if (!value) return;

    const alreadyExists = existingOptions?.some(
      (option) =>
        normalizeModifiableTagKey(option.value) ===
        normalizeModifiableTagKey(value),
    );
    if (alreadyExists) {
      toast({
        variant: "destructive",
        description: t("bo.ui.multiSelect.duplicateTag", {
          defaultValue: "Cet élément existe déjà dans la liste",
        }),
      });
      toggleOption(value);
      setOpen(false);
      return;
    }

    createModifiableList({
      listKey: listKey,
      value,
      lang: lang,
    });
    toggleOption(value);
    setOpen(false);
  };

  const onOpenChange = (open: boolean) => {
    setOpen(open);
    if (!open) {
      form.reset();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild onClick={() => setOpen(true)}>
        <Button
          variant="ghost"
          className="flex-1 justify-center cursor-pointer w-full hover:bg-transparent hover:text-primary text-sm text-[#005D8D] font-semibold"
        >
          {t("addTagToModifiableList")}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-[475px] bg-white p-0">
        <DialogHeader className="border-b px-6 py-6">
          <DialogTitle>{t("addTagToModifiableList")}</DialogTitle>
        </DialogHeader>
        <FormProvider {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit, (e) =>
              console.error("invalid", e),
            )}
            className="flex flex-col gap-4 px-6 pb-6"
          >
            <TextField
              config={{ name: "value", type: "text", label: t("tagToAdd") }}
              control={control}
              getValues={getValues}
            />
          </form>
        </FormProvider>
        <DialogFooter className="flex justify-end gap-1 border-t pt-4 pb-4 px-6">
          <Button
            className="w-full"
            type="button"
            variant="outline"
            onClick={() => setOpen(false)}
          >
            {t("cancel")}
          </Button>
          <Button
            className="w-full"
            type="submit"
            onClick={form.handleSubmit(onSubmit, (e) =>
              console.error("invalid", e),
            )}
            disabled={!form.formState.isValid}
          >
            {t("add")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddTagToModifiableList;
